import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Eye } from 'lucide-react';
import type {
  CreativePreview,
  ManualCampaignForm,
  PreviewData,
  SelectedScreenMeta,
} from '@/features/campaigns/types/campaign';
import './manualBuilder.css';
import { ManualProgressRail } from './ProgressRail';
import { LivePreviewPanel } from './LivePreviewPanel';
import {
  buildCompatibilityCopy,
  normalizeDetectedOrientation,
} from './orientationAssist';
import { MbAlert } from './primitives';
import { ManualPreviewProvider } from './manualPreviewContext';

export type ManualBuilderLayoutProps = {
  currentStep: number;
  skipScreenStep?: boolean;
  maxStepReached?: number;
  onStepSelect?: (stepId: number) => void;
  stepNavDisabled?: boolean;
  topBreadcrumb?: ReactNode;
  campaignData: ManualCampaignForm;
  setCampaignData?: React.Dispatch<React.SetStateAction<ManualCampaignForm>>;
  creativePreview: CreativePreview;
  setCreativePreview?: React.Dispatch<React.SetStateAction<CreativePreview>>;
  errors?: Record<string, string>;
  clearError?: (field: string) => void;
  selectedScreensMeta: Record<string, SelectedScreenMeta>;
  previewData?: PreviewData;
  availableBalance?: number;
  detectingOrientation?: boolean;
  onDetectingChange?: (busy: boolean) => void;
  selectionSource?: 'auto' | 'manual' | null;
  onSelectRecommendedScreen?: () => void;
  onKeepSelection?: () => void;
  children: ReactNode;
  stickyBar?: ReactNode;
};

export function ManualBuilderLayout({
  currentStep,
  skipScreenStep,
  maxStepReached,
  onStepSelect,
  stepNavDisabled = false,
  topBreadcrumb,
  campaignData,
  setCampaignData,
  creativePreview,
  setCreativePreview,
  errors = {},
  clearError,
  selectedScreensMeta,
  previewData,
  availableBalance,
  detectingOrientation = false,
  onDetectingChange,
  selectionSource = null,
  onSelectRecommendedScreen,
  onKeepSelection,
  children,
  stickyBar,
}: ManualBuilderLayoutProps) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return true;
  });

  // Live Preview opens with campaign creation (docked on desktop).
  // User can toggle anytime; preference sticks for this builder session only.
  const [previewOpen, setPreviewOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 1280;
  });

  const togglePreview = useCallback((next?: boolean) => {
    setPreviewOpen((prev) => {
      const value = typeof next === 'boolean' ? next : !prev;
      try {
        sessionStorage.setItem('screenista.mbLivePreview', value ? '1' : '0');
      } catch {
        /* ignore */
      }
      return value;
    });
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1280px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!previewOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') togglePreview(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    // Lock page scroll only for overlay (tablet/mobile), not docked desktop
    let prevOverflow = '';
    if (!isDesktop) {
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (!isDesktop) document.body.style.overflow = prevOverflow;
    };
  }, [previewOpen, togglePreview, isDesktop]);

  const creativeOrientation = normalizeDetectedOrientation(
    campaignData.mediaIds?.[0]?.orientation,
  );
  const firstScreen = Object.values(selectedScreensMeta)[0];
  const compatibility = useMemo(
    () =>
      buildCompatibilityCopy({
        detecting: detectingOrientation,
        creative: creativeOrientation,
        screenOrientation: firstScreen?.screenOrientation,
        selectionSource,
      }),
    [
      detectingOrientation,
      creativeOrientation,
      firstScreen?.screenOrientation,
      selectionSource,
    ],
  );

  const showCompatInBuilder =
    compatibility &&
    (compatibility.tone === 'danger' || compatibility.tone === 'warn') &&
    (currentStep === 1 || currentStep === 2 || currentStep === 4);

  const previewPanel = (
    <LivePreviewPanel
      currentStep={currentStep}
      campaignData={campaignData}
      setCampaignData={setCampaignData}
      creativePreview={creativePreview}
      setCreativePreview={setCreativePreview}
      errors={errors}
      clearError={clearError}
      selectedScreensMeta={selectedScreensMeta}
      previewData={previewData}
      detectingOrientation={detectingOrientation}
      onDetectingChange={onDetectingChange}
      availableBalance={availableBalance}
      onClose={() => togglePreview(false)}
    />
  );

  // Tablet/mobile: overlay drawer (does not need to reserve layout space)
  const overlayDrawer =
    previewOpen && !isDesktop && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Screenista Live Display Preview"
            className="fixed inset-0 z-[95] flex justify-end"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/50 backdrop-blur-[1px] transition-opacity duration-300"
              aria-label="Close preview"
              onClick={() => togglePreview(false)}
            />
            <div className="mb-preview-drawer relative z-10 flex h-full w-full max-w-md flex-col overflow-hidden bg-white shadow-2xl sm:max-w-[420px]">
              {previewPanel}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <ManualPreviewProvider
      value={{
        previewOpen,
        openPreview: () => togglePreview(true),
        closePreview: () => togglePreview(false),
        togglePreview: () => togglePreview(),
      }}
    >
      <div className="mb-shell camp-fade-up h-full max-h-full min-h-0 flex-1">
        <div
          className={`mb-shell__grid h-full max-h-full min-h-0 flex-1 ${
            previewOpen && isDesktop ? 'mb-shell__grid--preview-open' : ''
          }`}
        >
          <div className="mb-shell__main flex h-full min-h-0 w-full max-w-full flex-1 flex-col">
            <div className="mb-1 flex shrink-0 items-center justify-between gap-2.5">
              <div className="min-w-0 flex-1">{topBreadcrumb}</div>

              <button
                type="button"
                onClick={() => togglePreview()}
                className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-xs transition-all duration-200 ${
                  previewOpen
                    ? 'bg-slate-900 text-white shadow-slate-900/15 hover:bg-slate-800'
                    : 'border border-[var(--dash-line)] bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
                aria-expanded={previewOpen}
                aria-label={
                  previewOpen ? 'Hide preview panel' : 'Open live preview'
                }
              >
                <Eye className="h-3.5 w-3.5 text-current" />
                <span className="hidden sm:inline">
                  {previewOpen ? 'Hide Preview' : 'Live Preview'}
                </span>
                <span className="sm:hidden">
                  {previewOpen ? 'Hide' : 'Preview'}
                </span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    previewOpen
                      ? 'bg-emerald-400'
                      : creativePreview.url ||
                          (creativeOrientation &&
                            creativeOrientation !== 'EMPTY')
                        ? 'bg-emerald-500'
                        : 'bg-slate-300'
                  }`}
                />
              </button>
            </div>

            <div className="mb-progress shrink-0">
              <ManualProgressRail
                currentStep={currentStep}
                skipScreenStep={skipScreenStep}
                maxStepReached={maxStepReached}
                onStepSelect={onStepSelect}
                disabled={stepNavDisabled}
              />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
              {showCompatInBuilder ? (
                <MbAlert
                  tone={compatibility.tone}
                  title={compatibility.title}
                  actions={
                    compatibility.tone === 'danger' ? (
                      <>
                        {onSelectRecommendedScreen ? (
                          <button
                            type="button"
                            className="rounded-lg bg-[var(--dash-brand)] px-3 py-1.5 text-[11px] font-semibold text-white"
                            onClick={onSelectRecommendedScreen}
                          >
                            Select recommended screen
                          </button>
                        ) : null}
                        {onKeepSelection ? (
                          <button
                            type="button"
                            className="rounded-lg border border-current/30 bg-white/80 px-3 py-1.5 text-[11px] font-semibold"
                            onClick={onKeepSelection}
                          >
                            Keep current selection
                          </button>
                        ) : null}
                      </>
                    ) : null
                  }
                >
                  {compatibility.body}
                </MbAlert>
              ) : null}

              {children}
            </div>

            {stickyBar ? <div className="shrink-0 pt-2">{stickyBar}</div> : null}
          </div>

          {/* Desktop docked preview — always mounted so width can animate smoothly */}
          {isDesktop ? (
            <aside
              className={`mb-shell__aside ${previewOpen ? 'mb-shell__aside--open' : ''}`}
              aria-hidden={!previewOpen}
            >
              <div className="mb-shell__aside-inner">{previewPanel}</div>
            </aside>
          ) : null}
        </div>
      </div>
      {overlayDrawer}
    </ManualPreviewProvider>
  );
}
